import { BehaviorSubject } from 'rxjs';
import { fetchWrapper, history } from '../_helpers';

const userSubject = new BehaviorSubject(null);
const todayReservationCountSubject = new BehaviorSubject(0);
const futureReservationCountSubject = new BehaviorSubject(0);
const pastReservationCountSubject = new BehaviorSubject(0);
const configSubject = new BehaviorSubject(null);

const baseUrl = `/accounts`;

export const accountService = {
    login,
    googleLogin,
    logout,
    refreshToken,
    register,
    verifyEmail,
    forgotPassword,
    validateResetToken,
    resetPassword,
    addReservation,
    updateReservation,
    refundReservation,
    deleteReservation,
    openLockerForReservation,
    transferLockerForReservation,
    endRentalForReservation,
    activateReservation,
    getAllReservations,
    getActiveReservations,
    getPastReservations,
    getAdminReservations,
    getAll,
    getById,
    getReservationById,
    getSeasonPassPricing,
    getLockersForReassign,
    create,
    update,
    delete: _delete,
    todayReservationCount: todayReservationCountSubject.asObservable(),
    futureReservationCount: futureReservationCountSubject.asObservable(),
    pastReservationCount: pastReservationCountSubject.asObservable(),
    user: userSubject.asObservable(),
    config: configSubject.asObservable(),
    configuration,
    get userValue() { return userSubject.value },
    get todayReservationCountValue() { return todayReservationCountSubject.value },
    get futureReservationCountValue() { return futureReservationCountSubject.value },
    get pastReservationCountValue() { return pastReservationCountSubject.value },
    get configValue() { return configSubject.value },
};

function login(email, password) {
    return fetchWrapper.post(`${baseUrl}/authenticate`, { email, password })
        .then(user => {
          // publish user to subscribers and start timer to refresh token
          userSubject.next(user);
          todayReservationCountSubject.next(user.todayReservationCount);
          futureReservationCountSubject.next(user.futureReservationCount);
          pastReservationCountSubject.next(user.pastReservationCount);
          startRefreshTokenTimer();
          return user;
        });
}

function googleLogin(id_token) {
    return fetchWrapper.post(`${baseUrl}/google_authenticate`, { id_token })
        .then(user => {
            // publish user to subscribers and start timer to refresh token
            userSubject.next(user);
            todayReservationCountSubject.next(user.todayReservationCount);
            futureReservationCountSubject.next(user.futureReservationCount);
            pastReservationCountSubject.next(user.pastReservationCount);
            startRefreshTokenTimer();


            return user;
        });
}

function logout(event) {

    if (typeof event !== 'undefined') {
      event.preventDefault();
    }
    // revoke token, stop refresh timer, publish null to user subscribers and redirect to login page
    fetchWrapper.post(`${baseUrl}/revoke-token`, {});
    stopRefreshTokenTimer();
    userSubject.next(null);
    history.push('/account/login');
}

function configuration() {
  return fetchWrapper.post(`${baseUrl}/configuration`, {})
    .then(config => {

      var ctmp = {};
      config.forEach(entry => {
        ctmp[entry.name] = entry.value;
      });

      configSubject.next(ctmp);
      return ctmp;
    });
}

function refreshToken() {
    return fetchWrapper.post(`${baseUrl}/refresh-token`, {})
        .then(user => {
            // publish user to subscribers and start timer to refresh token
            userSubject.next(user);
            todayReservationCountSubject.next(user.todayReservationCount);
            futureReservationCountSubject.next(user.futureReservationCount);
            pastReservationCountSubject.next(user.pastReservationCount);
            startRefreshTokenTimer();
            return user;
        });
}

function register(params) {
    return fetchWrapper.post(`${baseUrl}/register`, params);
}

function verifyEmail(token) {
    return fetchWrapper.post(`${baseUrl}/verify-email`, { token });
}

function forgotPassword(email) {
    return fetchWrapper.post(`${baseUrl}/forgot-password`, { email });
}

function validateResetToken(token) {
    return fetchWrapper.post(`${baseUrl}/validate-reset-token`, { token });
}

function addReservation(params) {
  return fetchWrapper.post(`${baseUrl}/add-reservation`, params);
}

function resetPassword({ token, password, confirmPassword }) {
    return fetchWrapper.post(`${baseUrl}/reset-password`, { token, password, confirmPassword });
}

function getAll({ page = 1, pageSize = 25, search = '' } = {}) {
    const params = new URLSearchParams({ page, pageSize, search });
    return fetchWrapper.get(`${baseUrl}?${params}`);
}

// Are we using the date and active parameters? I think they're being passed through searchString, not seperately
function getAdminReservations(searchString, date, active) {
  return fetchWrapper.get(`${baseUrl}/admin-reservations?search=`+searchString+","+date+","+active);
}

function getAllReservations() {
  return fetchWrapper.get(`${baseUrl}/reservations`)
    .then(list => {
      futureReservationCountSubject.next(list.length);
      return list;
    });  
}

function getActiveReservations() {
  return fetchWrapper.get(`${baseUrl}/active-reservations`)
    .then(list => {
      todayReservationCountSubject.next(list.length);
      return list;
    });  
}

function getPastReservations() {
    return fetchWrapper.get(`${baseUrl}/past-reservations`)
        .then(list => {
            pastReservationCountSubject.next(list.length);
            return list;
        });
}

function getById(id) {
    return fetchWrapper.get(`${baseUrl}/${id}`);
}

function getReservationById(id) {
  return fetchWrapper.get(`${baseUrl}/reservations/${id}`);
}

function getSeasonPassPricing(seasonpass,products,prices) {
  return fetchWrapper.get(`${baseUrl}/seasonpass-pricing?seasonPass=${seasonpass}&products=${products}&prices=${prices}`);
}

function getLockersForReassign(locker_id) {
  return fetchWrapper.get(`${baseUrl}/lockers-for-reassign/${locker_id}`);
}

function create(params) {
    return fetchWrapper.post(baseUrl, params);
}

function createReservation(params) {
  return fetchWrapper.post(`${baseUrl}/reservations/`, params);
}

function update(id, params) {
    return fetchWrapper.put(`${baseUrl}/${id}`, params)
        .then(user => {
            // update stored user if the logged in user updated their own record
            if (user.id === userSubject.value.id) {
                // publish updated user to subscribers
                user = { ...userSubject.value, ...user };
                userSubject.next(user);
            }
            return user;
        });
}

function updateReservation(id, params) {
  return fetchWrapper.put(`${baseUrl}/reservations/${id}`, params)
    .then(reservation => {
      return reservation;
    });
}

function refundReservation(id) {
  //uses payment controller
  return fetchWrapper.post(`/refund/${id}`)
    .then(reservation => {
      return reservation;
    });
}

// prefixed with underscore because 'delete' is a reserved word in javascript
function deleteReservation(id) {
  return fetchWrapper.delete(`${baseUrl}/reservations/${id}`)
    .then(x => {
      return x;
    });
}

function openLockerForReservation(id,gui_admin) {
  return fetchWrapper.post(`/lockers/locker-action`,
    {
      lockerNumber: gui_admin,
      accessCode: "0000",
      action: "bypassopen",
      reservationId: id
    }).then(x => {
      return x;
    }).catch(e => { throw 'System Error Opening Locker';});
}


function transferLockerForReservation(id, gui_admin) {
    throw 'Transfer not implemented!!';
    /*
    return fetchWrapper.post(`/lockers/locker-action`,
        {
            lockerNumber: gui_admin,
            accessCode: "0000",
            action: "bypassopen",
            reservationId: id
        }).then(x => {
            return x;
        }).catch(e => { throw 'System Error Opening Locker'; });
        */
}

function endRentalForReservation(id,gui_admin) {
    return fetchWrapper.post(`/lockers/locker-action`,
        {
            lockerNumber: gui_admin,
            accessCode: "0000",
            action: "bypassend",
            reservationId: id
        }).then(x => {
            getActiveReservations();
            getPastReservations();
            return x;
        }).catch(e => { throw 'System Error Ending Rental';});
}

function activateReservation(reservation_id,access_code) {
  return fetchWrapper.post(`/accounts/assign-rental`,
    {
      reservationId: reservation_id,
      accessCode: access_code,
    })
      .then(x => {
          getAllReservations();
      return x;
    });
}

// prefixed with underscore because 'delete' is a reserved word in javascript
function _delete(id) {
    return fetchWrapper.delete(`${baseUrl}/${id}`)
        .then(x => {
            // auto logout if the logged in user deleted their own record
            if (id === userSubject.value.id) {
                logout();
            }
            return x;
        });
}

// helper functions

let refreshTokenTimeout;

function startRefreshTokenTimer() {
    // parse json object from base64 encoded jwt token
    const jwtToken = JSON.parse(atob(userSubject.value.jwtToken.split('.')[1]));

    // set a timeout to refresh the token a minute before it expires
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - (60 * 1000);
    refreshTokenTimeout = setTimeout(refreshToken, timeout);
}

function stopRefreshTokenTimer() {
    clearTimeout(refreshTokenTimeout);
}
